import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import IconRound from 'assets/logos/icon-round.svg?react';
import ModuleCard from 'components/cards/ModuleCard';
import { Button } from 'components/ui';
import { colors } from 'design';
import usePreferences from 'hooks/usePreferences';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';

// Placeholder article id — to be swapped for the real announcement post once it's published.
const REBRANDING_ARTICLE_ID = '109';

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: 1,
    minWidth: 0,
  },
  closeButton: {
    color: colors.gradient.primary.on,
    flexShrink: 0,
    marginTop: -4,
    marginRight: -8,
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontWeight: 700,
    fontSize: '1rem',
    color: colors.brand.onPrimary,
    lineHeight: 1.3,
  },
  subtitle: {
    margin: '2px 0 0',
    fontSize: '0.82rem',
    color: colors.gradient.primary.on,
    lineHeight: 1.3,
  },
  arrow: {
    color: colors.gradient.primary.on,
    fontSize: '0.9em',
  },
});

export default function RebrandingAnnouncementCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();

  const { value: dismissed, set: dismiss, loading } = usePreferences(Keys.HOME_REBRANDING_ANNOUNCEMENT_DISMISSED);

  const shouldDisplay = !loading && dismissed !== true;
  if (!shouldDisplay) return null;

  const navigateToArticle = () => {
    posthog.capture('home.rebranding_announcement_clicked');
    dismiss('true');
    navigate(routes.article.route.replace(':index', REBRANDING_ARTICLE_ID));
  };
  const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && navigateToArticle();

  return (
    <ModuleCard accentBackground onClick={navigateToArticle} role="button" tabIndex={0} onKeyDown={handleKeyDown}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <IconRound fill={colors.brand.onPrimary} width={22} height={22} />
          </div>
          <div className={styles.textContainer}>
            <p className={styles.title}>{t('home.rebranding.title')}</p>
            <p className={styles.subtitle}>
              {t('home.rebranding.subtitle')}
              {/* A non-breaking space, so the caret can't wrap onto a line of its own — it stays glued to the last word. */}
              {' '}
              <RightOutlined className={styles.arrow} />
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          className={styles.closeButton}
          aria-label={t('home.rebranding.dismiss')}
          onClick={e => {
            e.stopPropagation();
            dismiss('true');
          }}
        >
          <CloseOutlined />
        </Button>
      </div>
    </ModuleCard>
  );
}
